<!--
  ============================================================================
  MadriLocalLanding — Landing institucional para captura de profesionales
  sanitarios en Madrid.

  Estratégia de copy (alinhada com briefing institucional):
    • Sujeto = "OligoCheck", no "yo". Tom de empresa, no de vendedor.
    • Vocabulario: "demostración privada", "demostración técnica",
      "profesionales sanitarios", "presentación exclusiva".
    • Escasez = agenda corporativa del especialista, no contador de urgencia.
    • Credenciales/respaldo institucional VIENEN ANTES de beneficios
      emocionales: 10+ años, +100 países, +12.000 profesionales,
      espectrofotometría validada.
    • Form simple (FormularioLead) → contato direto vía WhatsApp para
      agendamento. O filtro do WhatsApp qualifica melhor o lead.

  No-index: bloqueado em robots.txt (/madri-local). Não usa useSeo.
  Idioma: apenas ES (sem LangSwitcher, sem auto-idioma).
  Redirect pós-form: reaproveita /oligocheck/gracias.
  ============================================================================
-->
<template>
  <AppShell
    v-model:current-lang="currentLang"
    variante="produto"
    :mostrar-switcher="false"
    :auto-idioma="false"
  >
    <!-- ============================================================
         ALERTA TOPO — escassez institucional (no promocional).
         Fija arriba de todo el documento, refuerza a "agenda limitada"
         antes incluso del hero.
         ============================================================ -->
    <template #alerta>
      <AlertaTopo destaque="AGENDA EN MADRID:">
        hay un número limitado de franjas disponibles durante la visita del especialista. La asignación se realiza por orden de solicitud.
      </AlertaTopo>
    </template>

    <!-- ============================================================
         1. HERO INSTITUCIONAL
            Sequência institucional: bloco de respaldo (credenciales)
            ANTES do título emocional. Título focado em "demostración
            privada / presentación exclusiva" + cidade.
         ============================================================ -->
    <PageHero
      :logo-src="logoBranco"
      logo-alt="OligoCheck"
      badge="OligoCheck · Madrid"
      badge-variante="azul"
      subtitulo="Nuestro equipo técnico se desplazará a Madrid para presentar el equipo y realizar una demostración privada en su consulta. Disponibilidad limitada durante la visita del especialista."
    >
      <template #titulo>
        <span class="titulo-antes">Presentación exclusiva del equipo</span>
        <span class="ds-texto-gradiente">OligoCheck en Madrid</span>
        <span class="titulo-depois">para profesionales sanitarios.</span>
      </template>

      <template #ctas>
        <!-- Bloco institucional de respaldo (antes da headline emocional,
             como definido no briefing). -->
        <div class="respaldo-bloco">
          <div class="respaldo-item">
            <strong>+10 años</strong>
            <span>en el mercado europeo</span>
          </div>
          <div class="respaldo-divisor" aria-hidden="true"></div>
          <div class="respaldo-item">
            <strong>+100 países</strong>
            <span>en Europa y América Latina</span>
          </div>
          <div class="respaldo-divisor" aria-hidden="true"></div>
          <div class="respaldo-item">
            <strong>+12.000 profesionales</strong>
            <span>utilizan OligoCheck en su consulta</span>
          </div>
        </div>

        <div class="agenda-card">
          <div class="agenda-icone" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <div class="agenda-texto">
            <span class="agenda-label">Disponibilidad limitada</span>
            <p class="agenda-desc">
              La agenda del especialista en Madrid es reducida y se asigna
              por orden de solicitud. Reserve su franja para la demostración
              técnica en su consulta.
            </p>
          </div>
          <a href="#solicitar-demostracion" @click.prevent="scrollToForm" class="agenda-cta">
            Solicitar franja
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
            </svg>
          </a>
        </div>
      </template>

      <template #trust>
        <TrustBar
          :items="[
            { texto: 'Tecnología espectrofotométrica validada', icone: 'shield' },
            { texto: 'Demostración técnica · aproximadamente 1 hora', icone: 'clock' },
            { texto: 'Equipo técnico OligoCheck', icone: 'users' }
          ]"
        />
      </template>
    </PageHero>

    <!-- ============================================================
         2. QUÉ INCLUYE LA DEMOSTRACIÓN
            Vocabulario institucional: presentación, demostración en vivo,
            lectura técnica, sesión de consultas. Posicionamento como
            "servicio técnico", não "visita comercial".
         ============================================================ -->
    <SecaoPadrao id="demostracion" variante="alt">
      <SecaoHeader
        badge="Demostración técnica"
        titulo-antes="Qué incluye la"
        titulo-destaque="visita del especialista"
        subtitulo="Una sesión privada, sin compromiso, diseñada para que evalúe el equipo en el entorno real de su consulta y junto a su propio equipo profesional."
      />

      <div class="incluye-grid">
        <FeatureCard
          titulo="Presentación institucional"
          descricao="Recorrido completo por la tecnología OligoCheck, su base científica y los resultados clínicos documentados en consultas europeas."
        >
          <template #icone>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
          </template>
        </FeatureCard>

        <FeatureCard
          titulo="Demostración en vivo"
          descricao="Realización de una medición real en su consulta para que observe el flujo completo: lectura intracelular, interpretación y emisión del informe."
        >
          <template #icone>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </template>
        </FeatureCard>

        <FeatureCard
          titulo="Lectura técnica del informe"
          descricao="Análisis detallado de los más de 40 minerales, oligoelementos y metales pesados evaluados, con explicación clínica de cada parámetro."
        >
          <template #icone>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </template>
        </FeatureCard>

        <FeatureCard
          titulo="Sesión de consultas"
          descricao="Espacio reservado para que su equipo plantee dudas técnicas, escenarios clínicos y casos de uso específicos de su especialidad."
        >
          <template #icone>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
          </template>
        </FeatureCard>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         3. RESPALDO INSTITUCIONAL
            Tecnología + empresa + presencia global. Antes de qualquer
            ângulo emocional, reforço explícito de credenciais.
         ============================================================ -->
    <SecaoPadrao id="respaldo">
      <SecaoHeader
        badge="Respaldo institucional"
        titulo-antes="Una tecnología"
        titulo-destaque="con trayectoria"
        titulo-depois="y presencia internacional."
        subtitulo="OligoCheck es un equipo de uso profesional consolidado, presente en consultas, clínicas y centros de salud en Europa y América Latina."
      />

      <div class="respaldo-grid">
        <article class="respaldo-card">
          <span class="respaldo-card-num">+10</span>
          <span class="respaldo-card-label">Años de operación</span>
          <p>
            Más de una década perfeccionando la lectura espectrofotométrica
            aplicada a la práctica clínica, con actualizaciones continuas
            del software y del protocolo de medición.
          </p>
        </article>

        <article class="respaldo-card">
          <span class="respaldo-card-num">+12.000</span>
          <span class="respaldo-card-label">Profesionales sanitarios</span>
          <p>
            Médicos, nutricionistas, naturópatas, dentistas y otros
            profesionales utilizan OligoCheck como apoyo objetivo a sus
            recomendaciones de suplementación y estilo de vida.
          </p>
        </article>

        <article class="respaldo-card">
          <span class="respaldo-card-num">+100</span>
          <span class="respaldo-card-label">Países atendidos</span>
          <p>
            Presencia activa en Europa y América Latina, con red
            de distribución, soporte técnico local y formación continua
            disponible para los profesionales sanitarios.
          </p>
        </article>

        <article class="respaldo-card">
          <span class="respaldo-card-num respaldo-card-num--word">CE</span>
          <span class="respaldo-card-label">Tecnología validada</span>
          <p>
            Equipo basado en espectrofotometría, técnica consolidada en
            entornos clínicos y de laboratorio para el análisis cuantitativo
            de minerales, oligoelementos y metales pesados.
          </p>
        </article>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         4. PERFIL PROFESIONAL — para quem é
            Foco em "está pensado para" (linguagem institucional)
            ao invés de "imagine se você".
         ============================================================ -->
    <SecaoPadrao id="perfil" variante="alt">
      <SecaoHeader
        badge="Perfil profesional"
        titulo-antes="Diseñado para integrarse a"
        titulo-destaque="consultas profesionales"
        subtitulo="La demostración técnica está dirigida a profesionales sanitarios y centros que evalúan incorporar tecnología de análisis mineral intracelular a su práctica."
      />

      <div class="perfil-chips">
        <span v-for="p in perfis" :key="p" class="perfil-chip">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          {{ p }}
        </span>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         CTA INTERMEDIO — após "Perfil profesional".
         Único bloco intermediário da página: posição forte (usuário
         se identificou nos perfis) com copy de "agenda corporativa"
         (ângulo institucional, sem urgência promocional).
         ============================================================ -->
    <SecaoPadrao class="secao-cta-intermedio">
      <div class="cta-bloque">
        <div class="cta-bloque-icone" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
            <path d="M9 16l2 2 4-4"/>
          </svg>
        </div>
        <div class="cta-bloque-conteudo">
          <span class="cta-bloque-eyebrow">Reservar franja en Madrid</span>
          <h3 class="cta-bloque-titulo">
            ¿Le interesa esta <span class="ds-texto-gradiente">demostración en su consulta</span>?
          </h3>
          <p class="cta-bloque-desc">
            La agenda del especialista en Madrid se asigna por orden de solicitud. Coordine ahora la fecha y la franja horaria de su visita.
          </p>
        </div>
        <button type="button" class="cta-bloque-btn" @click="scrollToForm">
          Solicitar demostración técnica
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
          </svg>
        </button>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         5. ¿QUÉ ES OLIGOCHECK?
            Bloco educativo (importado da v2). Apresenta o produto a
            quem ainda não conhece e fecha com tech-box de
            espectrofotometría — credibilidade técnica antes do form.
         ============================================================ -->
    <SecaoPadrao id="que-es">
      <SecaoHeader
        badge="Conozca la tecnología"
        titulo-antes="¿Qué es"
        titulo-destaque="OligoCheck?"
        subtitulo="Un equipo profesional de análisis mineral intracelular que evalúa más de 40 oligoelementos y metales pesados directamente desde la palma de la mano del paciente, en cuestión de segundos."
      />

      <div class="features-grid">
        <FeatureCard
          titulo="Resultados en segundos"
          descricao="Análisis completo en menos de 5 segundos, con el informe disponible en la propia consulta."
        >
          <template #icone>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          </template>
        </FeatureCard>

        <FeatureCard
          titulo="No invasivo · sin agujas"
          descricao="Lectura óptica sobre la palma de la mano. El paciente no necesita preparación previa."
        >
          <template #icone>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/><line x1="17.5" y1="15" x2="9" y2="15"/></svg>
          </template>
        </FeatureCard>

        <FeatureCard
          titulo="Medición intracelular"
          descricao="Evalúa minerales, oligoelementos y metales pesados a nivel tisular, mediante interacción óptica directa."
        >
          <template #icone>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
          </template>
        </FeatureCard>

        <FeatureCard
          titulo="Informe profesional"
          descricao="Más de 40 parámetros con visualización gráfica, listo para interpretar y entregar al paciente."
        >
          <template #icone>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          </template>
        </FeatureCard>
      </div>

      <!-- BLOCO TÉCNICO: ESPECTROFOTOMETRÍA -->
      <div class="tech-box">
        <div class="tech-box-grid">
          <div class="tech-box-icone">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 12h4l3-9 4 18 3-9h4"/>
            </svg>
          </div>
          <div class="tech-box-conteudo">
            <div class="tech-box-label">La tecnología por detrás</div>
            <h3 class="tech-box-titulo">Espectrofotometría de absorción adaptada a tejidos vivos</h3>
            <p class="tech-box-desc">
              OligoCheck utiliza una variante patentada de la espectrofotometría
              de absorción óptica, técnica de referencia en laboratorios clínicos
              para cuantificar minerales. La medición se realiza directamente
              sobre el tejido vivo de la palma de la mano, de forma totalmente
              no invasiva y con precisión de grado laboratorial.
            </p>
            <ul class="tech-box-lista">
              <li>
                <div class="tech-step-body">
                  <strong>Emisión multiespectro</strong>
                  <span>Un módulo óptico emite haces de luz en longitudes de onda específicas (desde el UV cercano hasta el infrarrojo) calibradas individualmente para cada oligoelemento y metal analizado.</span>
                </div>
              </li>
              <li>
                <div class="tech-step-body">
                  <strong>Análisis del patrón de absorción</strong>
                  <span>Cada mineral absorbe la luz en una longitud de onda única. Sensores de alta sensibilidad miden la luz reflejada y calculan la concentración intracelular correspondiente.</span>
                </div>
              </li>
              <li>
                <div class="tech-step-body">
                  <strong>Cálculo algorítmico</strong>
                  <span>Un algoritmo propietario cruza los datos espectrales con una base comparativa de más de 2 millones de mediciones clínicas, generando valores cuantitativos de más de 40 parámetros en menos de 5 segundos.</span>
                </div>
              </li>
            </ul>
            <p class="tech-box-nota">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              Método registrado, no invasivo, sin radiación ionizante y aprobado para uso profesional.
            </p>
          </div>
        </div>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         6. FORMULÁRIO DE SOLICITAÇÃO
            FormularioLead com texto institucional. Redirect para
            /oligocheck/gracias (mesma página de obrigado existente).
         ============================================================ -->
    <SecaoPadrao id="solicitar-demostracion" variante="alt" class="secao-formulario">
      <FormularioLead
        idioma="es"
        :active-campaign="acConfig"
        :redirect-to="{ name: 'madri-local-gracias' }"
        titulo-badge="Solicitar demostración"
        titulo-parte1="Reserve una franja para la"
        titulo-destaque="demostración en Madrid"
        subtitulo="Complete sus datos y nuestro equipo se pondrá en contacto por WhatsApp para confirmar la fecha y la franja horaria de la demostración técnica en su consulta."
        texto-botao="Solicitar demostración técnica"
        @submitted="onLeadSubmitted"
      />

      <p class="form-aviso-institucional">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        Solicitud técnica sin compromiso. La información facilitada se
        utiliza exclusivamente para coordinar la demostración del equipo.
      </p>
    </SecaoPadrao>

    <template #rodape>
      <RodapeSite
        texto="Análisis mineral intracelular en tiempo real. Tecnología profesional para consultas sanitarias."
        direitos="Todos los derechos reservados."
      />
    </template>
  </AppShell>
</template>

<script setup>
import { ref } from 'vue'
import AppShell from '@/components/global/layout/AppShell.vue'
import AlertaTopo from '@/components/global/layout/AlertaTopo.vue'
import PageHero from '@/components/global/layout/PageHero.vue'
import RodapeSite from '@/components/global/layout/RodapeSite.vue'
import SecaoPadrao from '@/components/global/secao/SecaoPadrao.vue'
import SecaoHeader from '@/components/global/secao/SecaoHeader.vue'
import FeatureCard from '@/components/global/ui/FeatureCard.vue'
import TrustBar from '@/components/global/ui/TrustBar.vue'
import FormularioLead from '@/components/global/formulario/FormularioLead.vue'
import logoBranco from '@/assets/images/logo-branco.svg'
import { sendLeadToWebhook } from '@/lib/webhookLead'

/* Idioma fixo: esta landing é exclusiva para ES (Madrid).
   AppShell recebe `mostrar-switcher=false` e `auto-idioma=false`
   para que o LangSwitcher não apareça e o auto-detect por IP
   não troque o idioma por engano em visitantes fora da Espanha. */
const currentLang = ref('es')

/* ============================================================
   ActiveCampaign — form_43 (campanha Madrid).
   Atende v1 e v2 da landing /madri-local. Campos custom:
     field[1] = profisso (texto livre)
     field[2] = pais     (texto livre)
   ============================================================ */
const acConfig = {
  endpoint: 'https://eduardgasset95711.activehosted.com/proc.php',
  u: '43',
  f: '43',
  s: '',
  c: '0',
  m: '0',
  act: 'sub',
  v: '2',
  or: '25f5f87a-9bf1-4cb4-b419-7d187dd570fd'
}

/* Perfis sanitários elegíveis para a demonstração técnica.
   Mantém vocabulário ES institucional ("/a", profesionales). */
const perfis = [
  'Medicina general e integrativa',
  'Nutrición clínica y deportiva',
  'Naturopatía y terapias complementarias',
  'Estética avanzada y medicina estética',
  'Odontología y odontología integrativa',
  'Fisioterapia',
  'Farmacia con servicios sanitarios',
  'Centros de salud y bienestar'
]

const scrollToForm = () => {
  const el = document.getElementById('solicitar-demostracion')
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/* ============================================================
   Conversão → webhook Make.
   Junta dados do form + UTMs + cookies do Pixel + contexto da
   página e envia para o Make, que dispara a Conversions API do
   Meta server-side (token fica no Make, não aqui).
   ============================================================ */
const onLeadSubmitted = (payload) => {
  sendLeadToWebhook(payload, {
    contentName: 'madri_local_demo',
    contentCategory: 'demostracion_madrid',
    currency: 'EUR'
  })
}
</script>

<style scoped>
/* ============================================================
   HERO — overrides locais
   ============================================================ */
.titulo-antes,
.titulo-depois {
  font-family: var(--ds-font-display);
}

/* ============================================================
   BLOCO DE RESPALDO INSTITUCIONAL (acima do CTA do hero)
   Discreto, em linha, com divisores verticais — visual de
   "credenciales corporativas", não de banner promocional.
   ============================================================ */
.respaldo-bloco {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--ds-s-5);
  padding: var(--ds-s-4) var(--ds-s-5);
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-md);
  margin-bottom: var(--ds-s-6);
}

.respaldo-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2px;
  padding: 0 var(--ds-s-2);
}

.respaldo-item strong {
  font-family: var(--ds-font-bold);
  font-size: 16px;
  color: var(--ds-accent);
  letter-spacing: 0.2px;
}

.respaldo-item span {
  font-size: 12px;
  color: var(--ds-text-muted);
  line-height: 1.3;
  max-width: 180px;
}

.respaldo-divisor {
  width: 1px;
  height: 28px;
  background: var(--ds-border);
}

/* ============================================================
   CARD DE AGENDA (escassez institucional)
   "Disponibilidad limitada" — comunicado de agenda, não promo.
   ============================================================ */
.agenda-card {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--ds-s-5);
  padding: var(--ds-s-5) var(--ds-s-6);
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-accent-border);
  border-radius: var(--ds-r-lg);
  text-align: left;
  box-shadow: var(--ds-shadow-md);
}

.agenda-icone {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--ds-accent-soft);
  color: var(--ds-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.agenda-texto {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.agenda-label {
  font-family: var(--ds-font-bold);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--ds-accent);
}

.agenda-desc {
  font-size: 14px;
  color: var(--ds-text-muted);
  line-height: 1.5;
  margin: 0;
}

.agenda-cta {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-s-2);
  padding: 12px 20px;
  border-radius: var(--ds-r-full);
  background: var(--ds-gradient-warning);
  color: var(--ds-text);
  font-family: var(--ds-font-bold);
  font-size: 14px;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: var(--ds-shadow-glow-warning);
  transition: transform var(--ds-t-base), box-shadow var(--ds-t-base);
}

.agenda-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px -6px rgba(245, 130, 32, 0.55);
}

/* ============================================================
   SEÇÃO 2 — Qué incluye la demostración
   ============================================================ */
.incluye-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--ds-s-5);
}

/* ============================================================
   SEÇÃO 3 — Respaldo institucional (cards de números grandes)
   Visual sóbrio, sem ícones decorativos — números falam por si.
   ============================================================ */
.respaldo-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--ds-s-5);
}

.respaldo-card {
  background: var(--ds-surface-1);
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-r-lg);
  padding: var(--ds-s-7) var(--ds-s-6);
  display: flex;
  flex-direction: column;
  gap: var(--ds-s-2);
  transition: border-color var(--ds-t-base), background var(--ds-t-base);
}

.respaldo-card:hover {
  border-color: var(--ds-accent-border);
  background: var(--ds-surface-3);
}

.respaldo-card-num {
  font-family: var(--ds-font-display);
  font-size: 44px;
  line-height: 1;
  background: var(--ds-gradient-text);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  letter-spacing: -1px;
}

.respaldo-card-num--word {
  font-size: 38px;
}

.respaldo-card-label {
  font-family: var(--ds-font-bold);
  font-size: 12px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--ds-text-dim);
  margin-bottom: var(--ds-s-2);
}

.respaldo-card p {
  font-size: 14px;
  color: var(--ds-text-muted);
  line-height: 1.6;
  margin: 0;
}

/* ============================================================
   SEÇÃO 4 — Perfil profesional (chips)
   ============================================================ */
.perfil-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--ds-s-3);
  max-width: 880px;
  margin: 0 auto;
}

.perfil-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-s-2);
  padding: 10px 18px;
  border-radius: var(--ds-r-full);
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border);
  color: var(--ds-text-muted);
  font-size: 14px;
  transition: all var(--ds-t-base);
}

.perfil-chip :deep(svg),
.perfil-chip svg {
  color: var(--ds-ciano);
  flex-shrink: 0;
}

.perfil-chip:hover {
  border-color: var(--ds-accent-border);
  background: var(--ds-surface-3);
  color: var(--ds-text);
}

/* ============================================================
   SEÇÃO 5 — ¿Qué es OligoCheck? (features + tech-box)
   ============================================================ */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--ds-s-5);
}

.tech-box {
  margin-top: var(--ds-s-8);
  background:
    linear-gradient(135deg, rgba(20, 203, 240, 0.06) 0%, rgba(26, 175, 149, 0.04) 100%),
    rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(20, 203, 240, 0.2);
  border-radius: var(--ds-r-xl);
  padding: 36px;
  position: relative;
  overflow: hidden;
}

.tech-box::before {
  content: '';
  position: absolute;
  top: -60px;
  right: -60px;
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(20, 203, 240, 0.15) 0%, transparent 70%);
  pointer-events: none;
}

.tech-box-grid {
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 24px;
  position: relative;
  z-index: 1;
}

.tech-box-icone {
  width: 56px;
  height: 56px;
  border-radius: var(--ds-r-md);
  background: linear-gradient(135deg, rgba(20, 203, 240, 0.2), rgba(20, 203, 240, 0.08));
  border: 1px solid rgba(20, 203, 240, 0.3);
  color: var(--ds-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: var(--ds-shadow-glow-azul);
}

.tech-box-label {
  font-family: var(--ds-font-bold);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--ds-ciano);
  margin-bottom: 6px;
}

.tech-box-titulo {
  font-family: var(--ds-font-display);
  font-size: 22px;
  color: var(--ds-text);
  margin: 0 0 12px;
  line-height: 1.3;
  letter-spacing: -0.3px;
}

.tech-box-desc {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.6;
  margin: 0 0 22px;
}

.tech-box-lista {
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  counter-reset: tech-steps;
}

.tech-box-lista li {
  display: flex;
  gap: 14px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  align-items: flex-start;
  counter-increment: tech-steps;
  position: relative;
}

.tech-box-lista li::before {
  content: counter(tech-steps);
  width: 28px;
  height: 28px;
  border-radius: var(--ds-r-sm);
  background: var(--ds-accent-soft);
  color: var(--ds-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--ds-font-bold);
  font-size: 13px;
  border: 1px solid var(--ds-accent-border);
  flex-shrink: 0;
  margin-top: 1px;
}

.tech-step-body {
  flex: 1;
  min-width: 0;
}

.tech-box-lista li strong {
  display: block;
  font-family: var(--ds-font-bold);
  font-size: 14px;
  color: var(--ds-text);
  margin-bottom: 4px;
  line-height: 1.35;
}

.tech-box-lista li span {
  display: block;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.55;
}

.tech-box-nota {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-s-2);
  font-size: 12px;
  color: var(--ds-text-dim);
  margin: 0;
  padding-top: 14px;
  border-top: 1px solid var(--ds-border-subtle);
  font-style: italic;
}

.tech-box-nota svg {
  color: var(--ds-ciano);
  flex-shrink: 0;
}

@media (max-width: 720px) {
  .tech-box { padding: 26px 20px; border-radius: var(--ds-r-lg); }
  .tech-box-grid { grid-template-columns: 1fr; gap: var(--ds-s-4); }
  .tech-box-icone { width: 48px; height: 48px; }
  .tech-box-titulo { font-size: 19px; }
  .tech-box-desc { font-size: 14px; }
}

/* ============================================================
   FORMULÁRIO — aviso institucional discreto abaixo do form
   ============================================================ */
.secao-formulario {
  padding-top: var(--ds-s-10);
  padding-bottom: var(--ds-s-11);
}

.form-aviso-institucional {
  max-width: 560px;
  margin: var(--ds-s-5) auto 0;
  text-align: center;
  font-size: 12px;
  color: var(--ds-text-dim);
  line-height: 1.5;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ds-s-2);
}

.form-aviso-institucional svg {
  color: var(--ds-text-dim);
  flex-shrink: 0;
}

/* ============================================================
   CTA INTERMEDIO — bloco de conversão entre seções.
   Aparece duas vezes: após a Seção 2 (Demostración técnica) e
   após a Seção 4 (Perfil profesional). Reduz fricção para usuários
   que não querem ler até o final.
   ============================================================ */
.secao-cta-intermedio {
  padding-top: var(--ds-s-7);
  padding-bottom: var(--ds-s-7);
}

.cta-bloque {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--ds-s-6);
  max-width: 980px;
  margin: 0 auto;
  padding: var(--ds-s-6) var(--ds-s-7);
  background:
    linear-gradient(135deg, rgba(20, 203, 240, 0.06) 0%, rgba(245, 130, 32, 0.05) 100%),
    var(--ds-surface-2);
  border: 1px solid var(--ds-accent-border);
  border-radius: var(--ds-r-xl);
  box-shadow: var(--ds-shadow-md);
  position: relative;
  overflow: hidden;
}

.cta-bloque::before {
  content: '';
  position: absolute;
  top: -80px;
  right: -80px;
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(245, 130, 32, 0.18) 0%, transparent 70%);
  pointer-events: none;
}

.cta-bloque-icone {
  width: 52px;
  height: 52px;
  border-radius: var(--ds-r-md);
  background: var(--ds-accent-soft);
  color: var(--ds-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.cta-bloque-conteudo {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  z-index: 1;
  min-width: 0;
}

.cta-bloque-eyebrow {
  font-family: var(--ds-font-bold);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--ds-accent);
}

.cta-bloque-titulo {
  font-family: var(--ds-font-display);
  font-size: 22px;
  line-height: 1.25;
  color: var(--ds-text);
  margin: 0;
  letter-spacing: -0.3px;
}

.cta-bloque-desc {
  font-size: 14px;
  color: var(--ds-text-muted);
  line-height: 1.55;
  margin: 0;
  max-width: 520px;
}

.cta-bloque-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-s-2);
  padding: 14px 24px;
  border-radius: var(--ds-r-full);
  background: var(--ds-gradient-warning);
  color: var(--ds-text);
  font-family: var(--ds-font-bold);
  font-size: 14px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: var(--ds-shadow-glow-warning);
  transition: transform var(--ds-t-base), box-shadow var(--ds-t-base);
  position: relative;
  z-index: 1;
}

.cta-bloque-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px -6px rgba(245, 130, 32, 0.55);
}

.cta-bloque-btn:focus-visible {
  outline: 2px solid var(--ds-accent);
  outline-offset: 3px;
}

/* ============================================================
   RESPONSIVO
   ============================================================ */
@media (max-width: 900px) {
  .incluye-grid,
  .respaldo-grid {
    grid-template-columns: 1fr;
  }

  .agenda-card {
    grid-template-columns: 1fr;
    text-align: center;
    padding: var(--ds-s-5);
  }

  .agenda-icone {
    margin: 0 auto;
  }

  .agenda-cta {
    justify-content: center;
    width: 100%;
  }

  .respaldo-bloco {
    gap: var(--ds-s-3);
    padding: var(--ds-s-3) var(--ds-s-4);
  }

  .respaldo-divisor {
    display: none;
  }

  .respaldo-item {
    flex: 1 1 130px;
  }

  .cta-bloque {
    grid-template-columns: 1fr;
    text-align: center;
    padding: var(--ds-s-5);
    gap: var(--ds-s-4);
  }

  .cta-bloque-icone {
    margin: 0 auto;
  }

  .cta-bloque-conteudo {
    align-items: center;
  }

  .cta-bloque-desc {
    text-align: center;
  }

  .cta-bloque-btn {
    justify-content: center;
    width: 100%;
  }

  .cta-bloque-titulo {
    font-size: 20px;
  }
}

@media (max-width: 480px) {
  .respaldo-card-num {
    font-size: 36px;
  }

  .respaldo-card-num--word {
    font-size: 30px;
  }

  .agenda-desc {
    font-size: 13px;
  }

  .cta-bloque-titulo {
    font-size: 18px;
  }
}
</style>
